import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

import { AutoCompleteSelectUI, ButtonUI, ConfirmDialogUI, InputUI, PhoneBase } from '../Interface';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useConfig from '../../context/ConfigContext';
import { useLoader } from '../../provider/LoaderProvider';
import masterDataService from '../../service/masterData.service';
import assemblyDefinitionService from '../../service/assemblyDefinition.service';
import inventoryConversionService from '../../service/inventoryConversion.service';
import { cleanValues, generateAutocompleteItems, normalizeNumberInput, parseLocaleNumber } from '../../utils/helper';
import InventoryConversionDefinitionDialog from './InventoryConversionDefinitionDialog';

const MODE_CONFIG = {
  disassembly: {
    phoneName: 'InventoryDisassembly',
    title: 'Bontás',
    buttonText: 'Bontás indítása',
    loadButtonText: 'Összetevők betöltése',
    successMessage: 'A bontás sikeresen rögzítve lett.',
    conversionType: 'DISASSEMBLY',
    headLabel: 'Bontandó cikk',
    lineTitle: 'Összetevők',
    summaryHeadLabel: 'Bontandó összesen',
    summaryLinesLabel: 'Előálló tételek összesen',
  },
  assembly: {
    phoneName: 'InventoryAssembly',
    title: 'Összeállítás',
    buttonText: 'Összeállítás indítása',
    loadButtonText: 'Összetevők betöltése',
    successMessage: 'Az összeállítás sikeresen rögzítve lett.',
    conversionType: 'ASSEMBLY',
    headLabel: 'Előállítandó cikk',
    lineTitle: 'Összetevők',
    summaryHeadLabel: 'Előállítandó összesen',
    summaryLinesLabel: 'Felhasznált összetevők összesen',
  },
};

const createLineFromDefinition = (line, headQty) => {
  const lineQty = parseLocaleNumber(line?.qty);
  const normalizedHeadQty = parseLocaleNumber(headQty);
  const baseQty = Number.isFinite(lineQty) ? lineQty : 0;
  const qty = Number.isFinite(normalizedHeadQty) ? baseQty * normalizedHeadQty : baseQty;

  return {
    id: uuidv4(),
    sourceId: line?.id ?? null,
    itemCode: line?.itemCode ?? '',
    itemName: line?.item?.itemName ?? '',
    quantityUnitId: line?.item?.quantityUnit?.id ?? line?.item?.quantityUnitId ?? null,
    quantityUnitCode: line?.item?.quantityUnit?.code ?? '',
    baseQty,
    qtyInput: String(qty),
    qty,
  };
};

const InventoryConversionForm = ({ mode }) => {
  const definitionInputRef = useRef(null);
  const { showLoader, hideLoader } = useLoader();
  const { config, getConfig } = useConfig();

  const modeConfig = MODE_CONFIG[mode] || MODE_CONFIG.assembly;

  const [definitionQuery, setDefinitionQuery] = useState('');
  const [selectedDefinition, setSelectedDefinition] = useState(null);
  const [definitionDialogItems, setDefinitionDialogItems] = useState([]);
  const [definitionDialogOpen, setDefinitionDialogOpen] = useState(false);
  const [confirmDefinitionChangeOpen, setConfirmDefinitionChangeOpen] = useState(false);
  const [pendingDefinitionQuery, setPendingDefinitionQuery] = useState('');
  const [qtyInput, setQtyInput] = useState('1');
  const [note, setNote] = useState('');
  const [docLines, setDocLines] = useState([]);
  const [warehouseData, setWarehouseData] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [selectedWhsCode, setSelectedWhsCode] = useState('');
  const [selectedBinId, setSelectedBinId] = useState(null);
  const [isResolving, setIsResolving] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  useEffect(() => {
    Object.keys(config).length === 0 && getConfig();

    masterDataService.warehouseToForm().then((data) => {
      const rows = data ?? [];
      setWarehouseData(rows);
      setWarehouseOptions(generateAutocompleteItems(rows, 'name', 'code'));
      const defaultWhs = config?.defaultWarehouse || '';
      if (defaultWhs) {
        setSelectedWhsCode(defaultWhs);
      }
    });
  }, []);

  useEffect(() => {
    if (!config?.defaultWarehouse) {
      return;
    }

    setSelectedWhsCode((prev) => prev || config.defaultWarehouse);
  }, [config?.defaultWarehouse]);

  useEffect(() => {
    const whs = warehouseData.find((warehouse) => warehouse.code === selectedWhsCode);
    if (!whs?.binManaged) {
      setSelectedBinId(null);
      return;
    }

    const hasSelectedBin = (whs.bin ?? []).some((bin) => bin.binId === selectedBinId);
    if (!hasSelectedBin) {
      setSelectedBinId(whs.dispatchBin ?? null);
    }
  }, [selectedWhsCode, selectedBinId, warehouseData]);

  const selectedWarehouse = useMemo(
    () => warehouseData.find((warehouse) => warehouse.code === selectedWhsCode) ?? null,
    [selectedWhsCode, warehouseData]
  );
  const isBinManagedWarehouse = Boolean(selectedWarehouse?.binManaged);
  const binOptions = useMemo(
    () => generateAutocompleteItems(selectedWarehouse?.bin ?? [], 'binCode', 'binId'),
    [selectedWarehouse]
  );

  const focusDefinitionInput = () => {
    setTimeout(() => {
      definitionInputRef.current?.focus?.();
      definitionInputRef.current?.select?.();
    }, 0);
  };

  const syncDefinitionLines = (definition, headQtyValue) => {
    const parsedHeadQty = parseLocaleNumber(headQtyValue);
    if (!definition?.itemCode || !Number.isFinite(parsedHeadQty) || parsedHeadQty <= 0) {
      return [];
    }

    return (definition.assemblyDefinitionLine ?? []).map((line) => createLineFromDefinition(line, parsedHeadQty));
  };

  const loadSelectedDefinition = async (definitionId) => {
    const fullDefinition = await assemblyDefinitionService.getItemById(definitionId);
    setSelectedDefinition(fullDefinition);
    setDefinitionQuery('');
    setDocLines(syncDefinitionLines(fullDefinition, qtyInput));
    return fullDefinition;
  };

  const executeDefinitionSearch = async (query) => {
    setIsResolving(true);
    try {
      const data = await assemblyDefinitionService.searchItems({ query });

      if (!data?.length) {
        addNotification({
          content: `Nincs találat a megadott cikkre. (${query})`,
          type: NotificationType.WARNING,
        });
        return;
      }

      setDefinitionQuery('');

      if (data.length === 1) {
        await loadSelectedDefinition(data[0].id);
        return;
      }

      setDefinitionDialogItems(data);
      setDefinitionDialogOpen(true);
    } finally {
      setIsResolving(false);
    }
  };

  const handleSearchDefinition = async () => {
    const query = String(definitionQuery || '').trim();
    if (!query) {
      return;
    }

    if (docLines.length > 0 && query !== selectedDefinition?.itemCode) {
      setPendingDefinitionQuery(query);
      setConfirmDefinitionChangeOpen(true);
      return;
    }

    await executeDefinitionSearch(query);
  };

  const handleSelectDefinitionFromDialog = async (definition) => {
    await loadSelectedDefinition(definition.id);
    setDefinitionDialogItems([]);
    setDefinitionDialogOpen(false);
  };

  const loadDefinitionLines = () => {
    const headQty = parseLocaleNumber(qtyInput);

    if (!selectedDefinition?.itemCode) {
      addNotification({
        content: 'Először válassz egy összeállítást.',
        type: NotificationType.WARNING,
      });
      return;
    }

    if (!Number.isFinite(headQty) || headQty <= 0) {
      addNotification({
        content: 'A mennyiségnek 0-nál nagyobbnak kell lennie.',
        type: NotificationType.WARNING,
      });
      return;
    }

    const lines = (selectedDefinition.assemblyDefinitionLine ?? []).map((line) => createLineFromDefinition(line, headQty));
    setDocLines(lines);
  };

  const handleHeadQtyChange = (nextValue) => {
    const normalizedValue = normalizeNumberInput(nextValue);
    setQtyInput(normalizedValue);

    const parsedHeadQty = parseLocaleNumber(normalizedValue);
    if (!Number.isFinite(parsedHeadQty)) {
      setDocLines([]);
      return;
    }

    setDocLines((prev) =>
      prev.length === 0 && selectedDefinition?.itemCode
        ? syncDefinitionLines(selectedDefinition, normalizedValue)
        : prev.map((line) => {
        const qty = Number(line.baseQty) * parsedHeadQty;
        return {
          ...line,
          qtyInput: String(qty),
          qty,
        };
      })
    );
  };

  const handleLineQtyChange = (id) => (e) => {
    const normalizedValue = normalizeNumberInput(e.target.value);
    const parsedQty = parseLocaleNumber(normalizedValue);

    setDocLines((prev) =>
      prev.map((line) =>
        line.id === id
          ? {
              ...line,
              qtyInput: normalizedValue,
              qty: Number.isFinite(parsedQty) ? parsedQty : null,
            }
          : line
      )
    );
  };

  const handleRegister = async () => {
    const parsedHeadQty = parseLocaleNumber(qtyInput);
    const errors = [];

    if (!selectedDefinition?.itemCode) errors.push('Nincs kiválasztott összeállítás.');
    if (!Number.isFinite(parsedHeadQty) || parsedHeadQty <= 0) errors.push('A mennyiségnek 0-nál nagyobbnak kell lennie.');
    if (!selectedWhsCode) errors.push('Nincs kiválasztott raktár.');
    if (isBinManagedWarehouse && !selectedBinId) errors.push('Nincs kiválasztott tárhely.');
    if (docLines.length === 0) errors.push('Nincsenek betöltött összetevők.');
    if (docLines.some((line) => !Number.isFinite(Number(line.qty)) || Number(line.qty) <= 0)) {
      errors.push('Minden összetevő mennyiségének 0-nál nagyobbnak kell lennie.');
    }

    if (errors.length) {
      addNotification({
        content: errors.join('\n'),
        type: NotificationType.ERROR,
      });
      return;
    }

    const payload = cleanValues({
      conversionType: modeConfig.conversionType,
      docDate: dayjs().toDate(),
      itemCode: selectedDefinition.itemCode,
      qty: parsedHeadQty,
      note: note || null,
      whsCode: selectedWhsCode || null,
      binId: selectedBinId || null,
      inventoryConversionLine: docLines.map((line) => ({
        itemCode: line.itemCode,
        qty: Number(line.qty),
        whsCode: selectedWhsCode || null,
        binId: selectedBinId || null,
      })),
    });

    setIsRegistering(true);
    try {
      await inventoryConversionService.createItem(payload);

      addNotification({
        content: modeConfig.successMessage,
        type: NotificationType.SUCCESS,
      });

      setSelectedDefinition(null);
      setDefinitionQuery('');
      setQtyInput('1');
      setDocLines([]);
      setNote('');
      focusDefinitionInput();
    } catch (e) {
      addNotification({
        content: 'Hiba a rögzítés során.',
        type: NotificationType.ERROR,
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const parsedHeadQty = parseLocaleNumber(qtyInput);
  const headUnitCode = selectedDefinition?.item?.quantityUnit?.code ?? '';
  const docDateLabel = dayjs().format('YYYY.MM.DD HH:mm');
  const isValid =
    Boolean(selectedDefinition?.itemCode) &&
    Number.isFinite(parsedHeadQty) &&
    parsedHeadQty > 0 &&
    Boolean(selectedWhsCode) &&
    (!isBinManagedWarehouse || Boolean(selectedBinId)) &&
    docLines.length > 0 &&
    docLines.every((line) => Number.isFinite(Number(line.qty)) && Number(line.qty) > 0);

  return (
    <PhoneBase name={modeConfig.phoneName} sx={{ p: 2 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        {modeConfig.title}
      </Typography>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          minHeight: 80,
          mt: 1.5,
          p: 1.5,
          bgcolor: 'grey.100',
        }}
      >
        <Typography variant="h7" component="h4">
          {modeConfig.headLabel}
        </Typography>

        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          Dátum: {docDateLabel}
        </Typography>

        <Box sx={{ mt: 1, bgcolor: 'white' }}>
          <InputUI
            autoFocus
            fullWidth
            size="small"
            placeholder="Cikkszám keresése"
            value={definitionQuery}
            inputRef={definitionInputRef}
            onChange={(e) => setDefinitionQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearchDefinition();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" aria-label="Keresés" onClick={handleSearchDefinition} disabled={isResolving}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {selectedDefinition ? (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              mt: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'white',
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
              {[selectedDefinition?.itemCode, selectedDefinition?.item?.itemName].filter(Boolean).join(' - ')}
            </Typography>
            {selectedDefinition?.note ? (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ display: 'block', mt: 0.25 }}>
                {selectedDefinition.note}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
          <AutoCompleteSelectUI
            label="Raktár"
            InputLabelProps={{ shrink: Boolean(selectedWhsCode) }}
            selectedValue={selectedWhsCode}
            selectedLabelValue={warehouseOptions.find((warehouse) => warehouse.value === selectedWhsCode)?.label ?? ''}
            onChange={(_e, newVal) => {
              setSelectedWhsCode(newVal?.value ?? '');
            }}
            dataset={warehouseOptions}
          />
          {isBinManagedWarehouse ? (
            <AutoCompleteSelectUI
              label="Tárhely"
              InputLabelProps={{ shrink: Boolean(selectedBinId) }}
              selectedValue={selectedBinId}
              selectedLabelValue={(selectedWarehouse?.bin ?? []).find((bin) => bin.binId === selectedBinId)?.binCode ?? ''}
              onChange={(_e, newVal) => {
                setSelectedBinId(newVal?.value ?? null);
              }}
              dataset={binOptions}
            />
          ) : (
            <Box />
          )}
        </Box>

        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
            Mennyiség
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => {
                const currentQty = parseLocaleNumber(qtyInput);
                const nextQty = Number.isFinite(currentQty) ? Math.max(0, currentQty - 1) : 0;
                handleHeadQtyChange(String(nextQty));
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Box sx={{ flex: 1, bgcolor: 'white' }}>
              <InputUI
                fullWidth
                size="small"
                value={qtyInput}
                onChange={(e) => handleHeadQtyChange(e.target.value)}
              />
            </Box>
            <Box sx={{ minWidth: 40, textAlign: 'center' }}>{headUnitCode || ''}</Box>
            <IconButton
              size="small"
              onClick={() => {
                const currentQty = parseLocaleNumber(qtyInput);
                const nextQty = Number.isFinite(currentQty) ? currentQty + 1 : 1;
                handleHeadQtyChange(String(nextQty));
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          minHeight: 80,
          mt: 1.5,
          p: 1.5,
          bgcolor: 'grey.100',
        }}
      >
        <Typography variant="h7" component="h4">
          {modeConfig.lineTitle}
        </Typography>

        <Stack spacing={0.25} sx={{ mt: 1.5 }}>
          {docLines.map((line) => (
            <Box
              key={line.id}
              sx={{
                px: 1.5,
                py: 0.75,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'white',
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                {[line.itemCode, line.itemName].filter(Boolean).join(' - ')}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.75 }}>
                <Typography variant="caption" color="text.secondary">
                  Szükséges: {line.baseQty} {line.quantityUnitCode || ''}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {mode === 'disassembly' ? 'Bontandó:' : 'Összeállítandó:'}
                  </Typography>
                  <Box sx={{ width: 110 }}>
                    <InputUI
                      size="small"
                      value={line.qtyInput ?? ''}
                      onChange={handleLineQtyChange(line.id)}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {line.quantityUnitCode || ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          minHeight: 80,
          mt: 1.5,
          p: 1.5,
          bgcolor: 'grey.100',
        }}
      >
        <Typography variant="h7" component="h4">
          Megjegyzés
        </Typography>

        <Box sx={{ mt: 1, bgcolor: 'white' }}>
          <InputUI
            fullWidth
            multiline
            minRows={3}
            placeholder="Megjegyzés"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Box>
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          minHeight: 80,
          mt: 1.5,
          p: 1.5,
          bgcolor: 'grey.100',
        }}
      >
        <Typography variant="caption" color="text.secondary" component="div">
          {modeConfig.summaryHeadLabel}
        </Typography>
        <Typography variant="subtitle1" fontWeight={600}>
          {qtyInput || 0} {headUnitCode || ''} {selectedDefinition?.itemCode || ''}
        </Typography>

        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          {modeConfig.summaryLinesLabel}
        </Typography>
        <Typography variant="subtitle1" fontWeight={600}>
          {docLines.length} tétel
        </Typography>
      </Box>

      <Box textAlign="center" mt="2rem">
        <ButtonUI
          text={modeConfig.buttonText}
          className="bg-success"
          onClick={handleRegister}
          disabled={!isValid || isRegistering || isResolving}
        />
      </Box>

      <InventoryConversionDefinitionDialog
        open={definitionDialogOpen}
        handleClose={() => {
          setDefinitionDialogOpen(false);
          setDefinitionDialogItems([]);
          focusDefinitionInput();
        }}
        items={definitionDialogItems}
        onSelect={handleSelectDefinitionFromDialog}
      />

      <ConfirmDialogUI
        show={confirmDefinitionChangeOpen}
        onHide={() => {
          setConfirmDefinitionChangeOpen(false);
          setPendingDefinitionQuery('');
        }}
        handleChangeElement={async () => {
          const query = pendingDefinitionQuery;
          setConfirmDefinitionChangeOpen(false);
          setPendingDefinitionQuery('');
          await executeDefinitionSearch(query);
        }}
        title="Cikkváltás megerősítése"
        message="A jelenlegi összetevőlista törlődni fog. Folytatod az új cikk keresését?"
        confirmButtonText="Folytatás"
      />
    </PhoneBase>
  );
};

export default InventoryConversionForm;
